import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

export default function CreateAlly() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    celular: "",
    cedula: "",
    pais: "CO",
    passwordTemporal: "",
  });

  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdAlly, setCreatedAlly] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) setError("");
    if (success) setSuccess("");
  };

  const validateForm = () => {
    if (!form.nombre.trim()) return "El nombre es obligatorio";
    if (!form.apellido.trim()) return "El apellido es obligatorio";
    if (!form.correo.trim()) return "El correo es obligatorio";
    if (!form.celular.trim()) return "El celular es obligatorio";
    if (!form.cedula.trim()) return "La cédula es obligatoria";
    if (!form.pais.trim()) return "El país es obligatorio";
    if (!form.passwordTemporal.trim()) return "La contraseña temporal es obligatoria";
    if (form.passwordTemporal.trim().length < 6) {
      return "La contraseña temporal debe tener mínimo 6 caracteres";
    }
    return "";
  };

  const resetForm = () => {
    setForm({
      nombre: "",
      apellido: "",
      correo: "",
      celular: "",
      cedula: "",
      pais: "CO",
      passwordTemporal: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const payload = {
      name: form.nombre.trim(),
      lastName: form.apellido.trim(),
      email: form.correo.trim().toLowerCase(),
      phone: form.celular.trim(),
      dni: form.cedula.trim(),
      country: form.pais.trim(),
      temporaryPassword: form.passwordTemporal.trim(),
    };

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/allies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo crear el aliado");
      }

      setCreatedAlly({
        name: form.nombre.trim(),
        lastName: form.apellido.trim(),
        email: form.correo.trim().toLowerCase(),
        temporaryPassword: form.passwordTemporal.trim(),
      });

      setShowSuccessModal(true);
      setSuccess("");
      resetForm();
    } catch (err) {
      setError(err.message || "Ocurrió un error al crear el aliado");
    } finally {
      setLoading(false);
    }
  };

  const handleSendCredentialsEmail = async () => {
    if (!createdAlly) return;

    setSendingEmail(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/allies/send-credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: createdAlly.name,
          lastName: createdAlly.lastName,
          email: createdAlly.email,
          temporaryPassword: createdAlly.temporaryPassword,
        }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo enviar el correo");
      }

      setShowSuccessModal(false);
      setSuccess("Correo enviado correctamente");

      setTimeout(() => {
        navigate("/admin/aliados");
      }, 900);
    } catch (err) {
      setError(err.message || "Ocurrió un error al enviar el correo");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate("/admin/aliados");
  };

  return (
    <div className="admin-page">
      <div className="table-page-header">
        <div>
          <h2>Crear aliado</h2>
        </div>
      </div>

      <form className="admin-form-card" onSubmit={handleSubmit}>
        <div className="admin-form-grid">
          <div className="form-group">
            <label>Nombre</label>
            <input
              type="text"
              name="nombre"
              value={form.nombre}
              onChange={handleChange}
              placeholder="Ingresa el nombre"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Apellido</label>
            <input
              type="text"
              name="apellido"
              value={form.apellido}
              onChange={handleChange}
              placeholder="Ingresa el apellido"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Correo</label>
            <input
              type="email"
              name="correo"
              value={form.correo}
              onChange={handleChange}
              placeholder="correo@ejemplo.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Celular</label>
            <input
              type="text"
              name="celular"
              value={form.celular}
              onChange={handleChange}
              placeholder="3001234567"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Cédula</label>
            <input
              type="text"
              name="cedula"
              value={form.cedula}
              onChange={handleChange}
              placeholder="Número de cédula"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>País</label>
            <select
              name="pais"
              value={form.pais}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="CO">Colombia</option>
              <option value="EC">Ecuador</option>
            </select>
          </div>

          <div className="form-group form-group-full">
            <label>Contraseña temporal</label>
            <input
              type="text"
              name="passwordTemporal"
              value={form.passwordTemporal}
              onChange={handleChange}
              placeholder="Contraseña temporal"
              disabled={loading}
            />
          </div>
        </div>

        {error ? <p className="admin-form-message error">{error}</p> : null}
        {success ? <p className="admin-form-message success">{success}</p> : null}

        <div className="form-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate("/admin/aliados")}
            disabled={loading}
          >
            Cancelar
          </button>

          <button type="submit" className="primary-btn" disabled={loading}>
            {loading ? "Creando..." : "Crear aliado"}
          </button>
        </div>
      </form>

      {showSuccessModal && createdAlly && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card">
            <h3>Aliado creado</h3>
            <p>
              El aliado <strong>{createdAlly.name} {createdAlly.lastName}</strong> fue
              creado exitosamente.
            </p>
            <p>Envíale sus credenciales para que pueda iniciar sesión.</p>

            <div className="admin-modal-actions">

              <button
                type="button"
                className="primary-btn"
                onClick={handleSendCredentialsEmail}
                disabled={sendingEmail}
              >
                {sendingEmail ? "Enviando..." : "Enviar correo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}